/**
 * DeleteConnectionDialog - shared confirmation dialog for deleting a
 * connection.
 *
 * Managed connections (ARM resources) and JSON-backed connections
 * (service-provider / agent / MCP entries inside connections.json) both need
 * the same "are you sure?" chrome: title, in-flight state on the Delete
 * button, red-tinted primary, and a Cancel that respects the busy flag. The
 * only real difference is the body copy, so the caller passes it in as a
 * ReactNode and this component owns the rest.
 */

import type { ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  destructiveButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    '&:hover': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      color: tokens.colorNeutralForegroundOnBrand,
    },
    '&:hover:active': {
      backgroundColor: tokens.colorPaletteRedForeground1,
      color: tokens.colorNeutralForegroundOnBrand,
    },
  },
});

export interface DeleteConnectionDialogProps {
  open: boolean;
  /** Body copy shown between the title and the buttons. */
  description: ReactNode;
  /** True while the parent's delete mutation is in flight. */
  isDeleting: boolean;
  /** Fired when the user clicks the primary Delete button. */
  onConfirm: () => void;
  /** Fired when Fluent's Dialog wants to close (backdrop, ESC, Cancel). */
  onOpenChange: (open: boolean) => void;
  /** Stable hook for tests to grab the surface. */
  testId?: string;
}

export const DeleteConnectionDialog = ({
  open,
  description,
  isDeleting,
  onConfirm,
  onOpenChange,
  testId,
}: DeleteConnectionDialogProps) => {
  const styles = useStyles();
  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        // Don't let a backdrop click cancel an in-flight delete — otherwise
        // the user loses their sense of "did that go through?".
        if (!data.open && isDeleting) return;
        onOpenChange(data.open);
      }}
    >
      <DialogSurface data-testid={testId}>
        <DialogBody>
          <DialogTitle>Delete connection?</DialogTitle>
          <DialogContent>
            <Text>{description}</Text>
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" disabled={isDeleting}>
                Cancel
              </Button>
            </DialogTrigger>
            <Button
              appearance="primary"
              onClick={onConfirm}
              disabled={isDeleting}
              className={styles.destructiveButton}
            >
              {isDeleting ? 'Deleting\u2026' : 'Delete'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

DeleteConnectionDialog.displayName = 'DeleteConnectionDialog';
